import AuthenticationVerification, {
  authenticationVerificationModalOverrides,
} from '@root/auth/src/components/authentication-verification';
import Button from '@root/core/src/components/button';
import Header from '@root/core/src/components/header';
import Link from '@root/core/src/components/link';
import Modal from '@root/core/src/components/modal';
import PhoneNumberInput from '@root/core/src/components/phone-number-input';
import PropTypes from '@root/vendor/prop-types';
import React, { useCallback, useState } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import SceneContent from '@root/core/src/components/scene-content';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import useForm from '@root/core/src/hooks/use-form';
import useMfaRequests from '@root/auth/src/hooks/use-mfa-requests';
import { BUTTON_VARIANT_PRIMARY } from '@root/core/src/components/button/styles';
import { Colors, StyleSheet, Theme } from '@root/core/src/utils/styles';
import { isMobileBrowser } from '@root/core/src/utils/detect-mobile-browser';
import { isRequired, isValidPhoneNumber } from '@root/core/src/utils/validators';

const validations = [
  isRequired('phoneNumber', 'Please enter your phone number'),
  isValidPhoneNumber(),
];

const Modals = {
  EXPLAINER: 'explainer',
  CODE_SENT: 'code_sent',
};

const RESEND_CODE_TIMEOUT_MS = 15000; // 15 seconds

export function PhoneNumberEntry({
  initialPhoneNumber,
  onMfaSetupComplete,
}) {
  const { trackClick } = useAnalytics('AUTH_PHONE_NUMBER_ENTRY');
  const { generateAndSendOtp, verifyOtp } = useMfaRequests();
  const [resendOtpLockedUntil, setResendOtpLockedUntil] = useState(null);

  const [currentModal, setCurrentModal] = useState(null);

  const generateAndSendOtpWithLock = useCallback(({ phoneNumber }) => {
    if (!resendOtpLockedUntil || resendOtpLockedUntil <= Date.now()) {
      setResendOtpLockedUntil(Date.now() + RESEND_CODE_TIMEOUT_MS);
      return generateAndSendOtp({ phoneNumber });
    }

    return Promise.resolve({
      success: false,
      locked: true,
    });
  }, [resendOtpLockedUntil, generateAndSendOtp]);

  const form = useForm({
    validations,
    initialValues: {
      phoneNumber: initialPhoneNumber,
    },
  });

  const phoneFieldProps = form.getFieldProps('phoneNumber');
  const phoneNumberValue = phoneFieldProps?.value;

  const openModalFactory = useCallback((modal) => () => {
    trackClick(`OPEN_${modal.toUpperCase()}_MODAL`);
    setCurrentModal(modal);
  }, [trackClick]);

  const closeModalFactory = useCallback((modal) => () => {
    trackClick(`CLOSE_${modal.toUpperCase()}_MODAL`);
    setCurrentModal(null);
  }, [trackClick]);

  const openExplainerModal = openModalFactory(Modals.EXPLAINER);
  const closeExplainerModal = closeModalFactory(Modals.EXPLAINER);
  const openCodeSentModal = openModalFactory(Modals.CODE_SENT);
  const closeCodeSentModal = closeModalFactory(Modals.CODE_SENT);

  const handleSubmit = ({ phoneNumber }) => {
    generateAndSendOtpWithLock({ phoneNumber });
    openCodeSentModal();
  };

  const handleVerification = (success) => {
    if (!success) {
      closeCodeSentModal();
    } else if (onMfaSetupComplete) {
      onMfaSetupComplete();
    }
  };

  return (
    <>
      <Header />
      <SceneContent>
        <form
          css={styles.form}
          onSubmit={form.createSubmitHandler(handleSubmit)}
        >
          <div css={styles.container}>
            <h1 css={styles.heading}>
              {'Protect your account with your phone number.'}
            </h1>
            <p css={styles.subtext}>
              {'You will need a U.S. phone number that can accept text messages. Message and data rates apply.'}
            </p>
          </div>
          <Link
            css={styles.link}
            onClick={openExplainerModal}
            type={'button'}
          >
            {'Why do you need my phone number?'}
          </Link>

          <PhoneNumberInput
            id={'phoneNumber'}
            label={'Phone number'}
            wrapperStyle={styles.phoneWrapper}
            {...phoneFieldProps}
          />

          <Button
            disabled={!form.isValid || form.submitting}
          >
            {'Send code'}
          </Button>
        </form>

        <Modal
          isShowing={currentModal === Modals.EXPLAINER}
          name={Modals.EXPLAINER}
          onCancel={closeExplainerModal}
          showCloseButton={false}
          styleOverrides={{
            innerContent: {
              ...Responsive.sm(
                {
                  padding: 0,
                  maxWidth: 450,
                }),
            },
          }}
        >
          <h1 css={styles.explainerHeading}>Why do you need my phone number?</h1>

          <div css={styles.explainerMessage}>
            <p>We need to text a security code to your phone number to verify that it’s really you. This extra layer of security is called Two-Factor Authentication (2FA) and helps ensure only you can access your account.</p>
            <p>Message and data rates apply.</p>
          </div>

          <Button
            onClick={closeExplainerModal}
            variant={BUTTON_VARIANT_PRIMARY}
          >
            Got it
          </Button>
        </Modal>

        {form.isValid && (
          <Modal
            closeOnClickOutside={false}
            isShowing={currentModal === Modals.CODE_SENT}
            name={Modals.CODE_SENT}
            onCancel={closeCodeSentModal}
            showCloseButton={!isMobileBrowser()}
            styleOverrides={authenticationVerificationModalOverrides}
          >
            <AuthenticationVerification
              onVerification={handleVerification}
              phoneNumber={phoneNumberValue}
              resendOtpLockedUntil={resendOtpLockedUntil}
              resendOtpRequest={generateAndSendOtpWithLock}
              resendOtpRequestPayload={{ phoneNumber: phoneNumberValue }}
              verifyOtpRequest={verifyOtp}
              verifyOtpRequestPayload={{ phoneNumber: phoneNumberValue }}
            />
          </Modal>
        )}
      </SceneContent>
    </>
  );
}

const styles = StyleSheet.create({
  form: {
    '& > *': { marginBottom: 24 },
    marginTop: 32,
  },
  phoneWrapper: {
    height: 'unset',
  },
  container: {
    ...Responsive.md({
      marginTop: 20,
      marginBottom: 30,
    }),
  },
  heading: {
    ...Theme.heading1(),
    whiteSpace: 'pre-line',
  },
  subtext: {
    ...Theme.paragraph1(),
    marginTop: 12,
  },
  link: {
    ...Theme.link(),
    color: Colors.gray50(),
  },
  explainerHeading: {
    ...Theme.heading1(),
    lineHeight: '110%',
  },
  explainerMessage: {
    ...Theme.paragraph1(),
    color: Colors.gray50(),
    marginBottom: 8,
  },
  modalInnerContent: {
    padding: 0,
  },
});

PhoneNumberEntry.propTypes = {
  initialPhoneNumber: PropTypes.string,
  onMfaSetupComplete: PropTypes.func,
};
